import { saveCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'
import { Heading } from '@cinch-labs/design-system'
import {
  SsrExperimentsProvider,
  useReportExperimentUI,
} from '@cinch-nx/adobe-server-side'
import { useStoryblokState } from '@cinch-nx/data-storyblok'
import { Head } from '@cinch-nx/landing-ui-shared'
import { Wrapper } from '@cinch-nx/shared-ui'
import { StoryblokRendererV2 as StoryblokRenderer } from '@cinch-nx/storyblok-utils'
import { Env, readFromEnv } from '@cinch-nx/environments'
import {
  OurCommitmentCarousel,
  ShopByShape,
  USPContainer,
  WeBuyAnyCar,
} from './components'
import { HeroAdditionalProps } from './components/hero-v2/hero'
import { NewCustomerTabs } from './components/new-customer-tabs'
import { ReturningCustomerTabs } from './components/returning-customer-tabs'
import { storyblokComponents } from './data/storyblok-renderer-map'
import type { HomepageProps } from './get-server-side-props'
import styles from './homepage.module.css'
import homePage from './locale/index'

export const LandingFeatureHomePage = ({
  preview,
  vehiclesData,
  story,
  usingStoryFallback,
  showPersonalisedComponents,
  pageMeta,
}: HomepageProps) => {
  story = useStoryblokState({
    story: story ?? undefined,
    enableBridge: preview,
  })
  const { ourCommitmentSlides, wbac, seo } = homePage

  if (preview && story.content.previewPersonalisedContent) {
    showPersonalisedComponents = story.content.previewPersonalisedContent
  }

  const personalisedContent = story?.content?.personalised?.[0]
  saveCustomerJourneyCookie()

  const isProd = process.env['STAGE_NAME'] === 'production'
  // TODO: uncomment when continue with favoriting on home when A&E is back
  // const showFavouritingTab =
  //   readFromEnv(Env.EnableFavouritingHomePageChanges) === 'true'
  // const { status: userStatus } = useUserStore()
  // const { favourites } = useFavouritesStore()
  // this console.logs are required in the ticket to prove we are getting favourites and user Status.
  // console.log('userStatus', userStatus)
  // console.log('favourites', favourites)

  const heroData: HeroAdditionalProps = {
    vehiclesData,
    storyFallback: usingStoryFallback,
  }

  const isAiSearchSection =
    story.content.herov2?.[0].component === 'AISearchSection'

  const aiSearchExperiment = readFromEnv(Env.AiSearchExperimentId)
  useReportExperimentUI(
    aiSearchExperiment,
    pageMeta.experiments.find(
      (experiment) => experiment.name === aiSearchExperiment,
    )?.experienceId === 1,
  )

  return (
    <SsrExperimentsProvider experiments={pageMeta.experiments}>
      <Head
        {...story}
        fallbackTitle={seo.title}
        fallbackDescription={seo.description}
      />
      {story.content.herov2 &&
        (isAiSearchSection ? (
          <Wrapper className={{ inner: styles['aiSearchSection'] }}>
            <StoryblokRenderer
              block={story.content.herov2}
              components={storyblokComponents}
              props={{ ...heroData }}
            />
          </Wrapper>
        ) : (
          <StoryblokRenderer
            block={story.content.herov2}
            components={storyblokComponents}
            props={{ ...heroData }}
          />
        ))}

      {story.content.usp && <USPContainer slides={story.content.usp} />}

      {/* TODO: uncomment when continue with favouriting on home when A&E is back */}
      {/* {showFavouritingTab && (
        <div className={styles['favouritingTabText']}>
          Favouriting feature is enabled!
        </div>
      )} */}
      {!showPersonalisedComponents &&
        story.content.usedCarsCarouselTab &&
        story.content.newCarsCarouselTab && (
          <Wrapper className={{ inner: styles['newUserTabbedCarousel'] }}>
            <NewCustomerTabs
              usedCarsCarouselTab={story?.content?.usedCarsCarouselTab}
              newCarsCarouselTab={story?.content?.newCarsCarouselTab}
            />
          </Wrapper>
        )}
      {showPersonalisedComponents ? (
        <Wrapper>
          <Heading element="h2" className={styles['heading']}>
            Welcome back
          </Heading>
          {story.content.newCarsCarouselTab && (
            <ReturningCustomerTabs
              newCarsTab={story.content.newCarsCarouselTab}
            />
          )}

          {personalisedContent.valuePropositions && (
            <div className={styles['valuePropsCardsContainer']}>
              <StoryblokRenderer
                block={personalisedContent.valuePropositions}
                components={storyblokComponents}
              />
            </div>
          )}

          {personalisedContent.helpMeChoose && (
            <StoryblokRenderer
              block={personalisedContent.helpMeChoose}
              components={storyblokComponents}
            />
          )}
        </Wrapper>
      ) : null}
      <ShopByShape count={vehiclesData.usedVehicles.carTypes} />

      {!showPersonalisedComponents && story.content.whyCinch && (
        <StoryblokRenderer
          block={story.content.whyCinch}
          components={storyblokComponents}
        />
      )}

      {story.content.partEx && (
        <StoryblokRenderer
          block={story.content.partEx}
          components={storyblokComponents}
        />
      )}

      {story.content.carsCarousel && (
        <Wrapper className={{ container: styles.section }}>
          <StoryblokRenderer
            block={story.content.carsCarousel}
            components={storyblokComponents}
          />
        </Wrapper>
      )}

      {!showPersonalisedComponents ? (
        <OurCommitmentCarousel {...ourCommitmentSlides} />
      ) : null}

      <WeBuyAnyCar {...wbac} />

      {isProd ? (
        <img
          alt="ZeroFox"
          className={styles['zeroFox']}
          src="https://d21y75miwcfqoq.cloudfront.net/786a6533"
        />
      ) : null}
    </SsrExperimentsProvider>
  )
}

export default LandingFeatureHomePage
